import './style.modules.scss'

import { Spacer } from '@ampcore/adel-react'
import { SpacerSpacing } from '@ampcore/adel-react'
import { MaterialIcon, MaterialIconSize } from '@ampcore/adel-react'
import {
  AmpcoreMaterialIconId,
  BackgroundColor
} from '@ampcore/ampcom-contracts'
import { kebabCase } from '@ampcore/string-format'
import React, { useEffect, useRef, useState } from 'react'
import xss from 'xss'

import { useMediaQuery } from '../../hooks/useMediaQuery'
import { MEDIA_QUERIES } from '../../theme/media-queries'
import { Link } from '../../types/Link.type'
import { VerticalTabsBackgroundColor } from './VerticalTabsColor.type'

export type VerticalTab = {
  identifier: string
  title?: string
  heading?: string
  content?: string
  image?: string
  footer?: string
  imageAlignment?: string
  link?: Link
  leftCompound?: string // TODO: rename compound
  rightCompound?: string
  backgroundColor?: VerticalTabsBackgroundColor
}

export type VerticalTabsProps = {
  componentSpacing?: SpacerSpacing
  tab: VerticalTab[]
  isArticle?: boolean
  testId?: string
}

/** Width threshold (px) below which the component uses the article (narrow) layout */
const ARTICLE_WIDTH_THRESHOLD = 814

export const VerticalTabs = ({
  tab,
  testId,
  isArticle,
  componentSpacing
}: VerticalTabsProps) => {
  const [activeTabIndex, setActiveTabIndex] = useState(0)
  const [isMobileActiveExpanded, setIsMobileActiveExpanded] = useState(true)
  const [isNarrow, setIsNarrow] = useState(false)
  const containerRef = useRef<HTMLDivElement>(null)
  const tabRefs = useRef<(HTMLButtonElement | null)[]>([])
  const buttonRefs = useRef<(HTMLAnchorElement | null)[]>([])
  const focusTargetRef = useRef<'tab' | 'button' | null>(null)
  const tabCount = tab.length

  const isMobile = useMediaQuery(MEDIA_QUERIES.sm)

  useEffect(() => {
    if (!containerRef.current || typeof ResizeObserver === 'undefined') return

    const observer = new ResizeObserver(entries => {
      for (const entry of entries) {
        setIsNarrow(entry.contentRect.width < ARTICLE_WIDTH_THRESHOLD)
      }
    })

    observer.observe(containerRef.current)
    return () => observer.disconnect()
  }, [])

  const useArticleLayout = isArticle || isNarrow
  function getTabColor(bgColor?: VerticalTabsBackgroundColor) {
    switch (bgColor) {
      case BackgroundColor.MINT_TINT:
        return 'u-bgColorMintLight10'
      case BackgroundColor.PURPLE_TINT:
        return 'u-bgColorPurpleLight10'
      case BackgroundColor.LIME_TINT:
        return 'u-bgColorLimeLight20'
      case BackgroundColor.WHITE:
        return 'u-bgColorWhite'
      case BackgroundColor.BERRY_TINT:
        return 'u-bgColorBerryLight10'
      case BackgroundColor.ORANGE_TINT:
        return 'u-bgColorOrangeLight20'
      default:
        return 'u-bgColorStone'
    }
  }

  useEffect(() => {
    if (focusTargetRef.current === 'tab') {
      tabRefs.current[activeTabIndex]?.focus()
    } else if (focusTargetRef.current === 'button') {
      buttonRefs.current[activeTabIndex]?.focus()
    }
    focusTargetRef.current = null
  }, [activeTabIndex])

  const handleTabClick = (index: number) => {
    tabRefs.current[index]?.scrollIntoView({
      behavior: 'smooth',
      block: 'nearest',
      inline: 'center'
    })

    if (activeTabIndex === index) {
      setIsMobileActiveExpanded(prev => !prev)
      return
    }

    setActiveTabIndex(index)
    setIsMobileActiveExpanded(true)
  }

  const handleTabKeyDown = (
    event: React.KeyboardEvent<HTMLButtonElement | HTMLAnchorElement>,
    origin: 'button' | 'tabpanel'
  ) => {
    if (event.key === 'Tab') {
      if (event.shiftKey) {
        event.stopPropagation()
        if (origin === 'tabpanel') {
          if (activeTabIndex === 0) {
            return
          }
          focusTargetRef.current = 'button'
          setActiveTabIndex(prev => (prev === 0 ? 0 : prev - 1))
          event.preventDefault()
          return
        }
        if (origin === 'button') {
          const ref = tabRefs.current[activeTabIndex]
          if (ref) {
            ref.focus()
            event.preventDefault()
          }
        }
      } else {
        if (origin === 'tabpanel') {
          const ref = buttonRefs.current[activeTabIndex]
          if (ref) {
            ref.focus()
            event.preventDefault()
          } else if (activeTabIndex < tabCount - 1) {
            tabRefs.current[activeTabIndex + 1]?.focus()
            setActiveTabIndex(prev => prev + 1)
            event.preventDefault()
          }
          return
        }
        if (origin === 'button') {
          if (activeTabIndex < tabCount - 1) {
            tabRefs.current[activeTabIndex + 1]?.focus()
            setActiveTabIndex(prev => prev + 1)
            event.preventDefault()
          }
        }
      }
    }
  }

  /** Renders left-column content (text, link, footer — no leftCompound images) */
  const renderLeftContent = (tabItem: VerticalTab, index: number) => (
    <>
      {tabItem.content && (
        <div
          className="Content content-block u-textSizeMsrp1 u-paddingBottom24"
          data-testid={testId ? `${testId}-html-content-${index}` : undefined}
          dangerouslySetInnerHTML={{ __html: xss(tabItem.content || '') }}
        />
      )}
      {tabItem.leftCompound && (
        <div
          className="u-paddingBottom24"
          data-testid={testId ? `${testId}-left-compound-${index}` : undefined}
          dangerouslySetInnerHTML={{ __html: xss(tabItem.leftCompound) }}
        />
      )}
      {tabItem.link && (
        <div className="VerticalTabs-linkWrapper">
          <a
            ref={el => (buttonRefs.current[index] = el)}
            href={tabItem.link.url}
            className="link-btn Button Button--primary"
            data-analytics={tabItem.link.analyticsName}
            target={tabItem.link.target}
            data-testid={testId ? `${testId}-link-${index}` : undefined}
            onKeyDown={event => handleTabKeyDown(event, 'button')}
          >
            {tabItem.link.name}
          </a>
        </div>
      )}
      {tabItem.footer && (
        <footer
          className="VerticalTabs-tabFooter u-marginTop20"
          data-testid={testId ? `${testId}-footer-${index}` : undefined}
        >
          <div dangerouslySetInnerHTML={{ __html: xss(tabItem.footer) }} />
        </footer>
      )}
    </>
  )

  /** Renders right-column content (rightCompound or image) */
  const renderRightContent = (tabItem: VerticalTab, index: number) => (
    <>
      {tabItem.rightCompound && (
        <div
          className="Content"
          data-testid={testId ? `${testId}-right-compound-${index}` : undefined}
          dangerouslySetInnerHTML={{ __html: xss(tabItem.rightCompound) }}
        />
      )}
      {tabItem.image && (
        <div
          className={`VerticalTabs-imageArea${
            tabItem.image.includes('<figure')
              ? ' VerticalTabs-imageArea--figure'
              : ''
          }`}
          data-testid={testId ? `${testId}-image-${index}` : undefined}
          dangerouslySetInnerHTML={{ __html: xss(tabItem.image) }}
        />
      )}
    </>
  )

  /** Renders all content in a single column (mobile / article mode — no compound images) */
  const renderAllContent = (tabItem: VerticalTab, index: number) => (
    <>{renderLeftContent(tabItem, index)}</>
  )

  if (isMobile) {
    // Mobile: Accordion layout
    return (
      <Spacer spacing={componentSpacing}>
        <section className="VerticalTabs u-posRelative u-sizeConstrained">
          <div
            className={`VerticalTabs-container u-sizeConstrained ${getTabColor(
              tab[activeTabIndex]?.backgroundColor
            )}`}
          >
            <div className="VerticalTabs-tabList u-paddingVert24">
              <ul className="List List--selectable">
                {tab.map((tabItem, index) => (
                  <React.Fragment key={`${tabItem.identifier}-${index}`}>
                    <button
                      role="tab"
                      className={`List-item u-textSizeMsrp1 u-textLeft u-sizeFull u-paddingVert20 u-flex u-flexJustifyBetween u-flexAlignItemsCenter ${
                        activeTabIndex === index ? 'is-active' : ''
                      } ${
                        activeTabIndex === index && isMobileActiveExpanded
                          ? 'VerticalTabs-tabButton--mobileExpanded'
                          : ''
                      } u-textColorActionBlue`}
                      aria-selected={activeTabIndex === index}
                      aria-controls={`${tabItem.identifier}-${index}`}
                      id={`vert-tab-btn-${index}`}
                      onClick={() => handleTabClick(index)}
                      ref={el => (tabRefs.current[index] = el)}
                      onKeyDown={event => {
                        if (!(index === tabCount - 1 && !event.shiftKey)) {
                          handleTabKeyDown(event, 'tabpanel')
                        }
                      }}
                      tabIndex={activeTabIndex === index ? 0 : -1}
                      data-analytics={
                        tabItem.title
                          ? `${kebabCase(tabItem.title)}-btn`
                          : undefined
                      }
                      data-testid={
                        testId ? `${testId}-tab-btn-${index}` : undefined
                      }
                    >
                      <span
                        dangerouslySetInnerHTML={{
                          __html: xss(tabItem.title || '')
                        }}
                      />
                      <MaterialIcon
                        icon={
                          activeTabIndex === index && isMobileActiveExpanded
                            ? AmpcoreMaterialIconId.EXPAND_LESS
                            : AmpcoreMaterialIconId.EXPAND_MORE
                        }
                        size={MaterialIconSize.SIZE_24}
                        className="VerticalTabs-chevron"
                        ariaLabel={
                          activeTabIndex === index && isMobileActiveExpanded
                            ? 'Collapse tab content'
                            : 'Expand tab content'
                        }
                      />
                    </button>
                    {activeTabIndex === index && isMobileActiveExpanded && (
                      <div
                        id={`${tabItem.identifier}-${index}`}
                        role="tabpanel"
                        data-testid={
                          testId ? `${testId}-tabpanel-${index}` : undefined
                        }
                        className={`VerticalTabs-tabPanel u-paddingHoriz24${
                          index === tabCount - 1
                            ? ' VerticalTabs-tabPanel--last'
                            : ''
                        }`}
                      >
                        {renderAllContent(tabItem, index)}
                      </div>
                    )}
                  </React.Fragment>
                ))}
              </ul>
            </div>
          </div>
        </section>
      </Spacer>
    )
  }

  // Desktop/Tablet layout
  return (
    <Spacer spacing={componentSpacing}>
      <section className="VerticalTabs u-posRelative u-sizeConstrained">
        <div
          ref={containerRef}
          className={`VerticalTabs-container u-sizeConstrained ${getTabColor(
            tab[activeTabIndex]?.backgroundColor
          )} ${useArticleLayout ? ' VerticalTabs-container--article' : ''}`}
        >
          <div className="VerticalTabs-tabList u-flex u-paddingVert24">
            <ul className="List List--selectable">
              {tab.map((tabItem, index) => (
                <React.Fragment key={`${tabItem.identifier}-${index}`}>
                  <button
                    role="tab"
                    className={`List-item u-textSizeMsrp1 u-textLeft u-sizeFull u-paddingVert20 u-flex u-flexJustifyBetween u-flexAlignItemsCenter ${
                      activeTabIndex === index ? 'is-active' : ''
                    } u-textColorActionBlue`}
                    aria-selected={activeTabIndex === index}
                    aria-controls={`${tabItem.identifier}-${index}`}
                    id={`vert-tab-btn-${index}`}
                    onClick={() => handleTabClick(index)}
                    ref={el => (tabRefs.current[index] = el)}
                    onKeyDown={event => {
                      if (!(index === tabCount - 1 && !event.shiftKey)) {
                        handleTabKeyDown(event, 'tabpanel')
                      }
                    }}
                    tabIndex={activeTabIndex === index ? 0 : -1}
                    data-analytics={
                      tabItem.title
                        ? `${kebabCase(tabItem.title)}-btn`
                        : undefined
                    }
                    data-testid={
                      testId ? `${testId}-tab-btn-${index}` : undefined
                    }
                  >
                    <span
                      dangerouslySetInnerHTML={{
                        __html: xss(tabItem.title || '')
                      }}
                    />
                  </button>
                </React.Fragment>
              ))}
            </ul>
          </div>
          <div className="VerticalTabs-contentRegion Content">
            {tab.map((tabItem, index) => (
              <div
                key={`${tabItem.identifier}-${index}`}
                id={`${tabItem.identifier}-${index}`}
                role="tabpanel"
                hidden={activeTabIndex !== index}
                data-testid={testId ? `${testId}-tabpanel-${index}` : undefined}
                className="VerticalTabs-tabPanel"
              >
                {activeTabIndex === index && (
                  <div
                    className={`${
                      !useArticleLayout &&
                      (tabItem.image || tabItem.rightCompound)
                        ? 'Grid u-flex'
                        : ''
                    }`}
                  >
                    <div
                      className={
                        !useArticleLayout &&
                        (tabItem.image || tabItem.rightCompound)
                          ? 'u-size1of2 u-padding24 u-flex u-flexCol u-flexJustifyCenter'
                          : 'u-padding24 u-flex u-flexCol u-flexJustifyCenter'
                      }
                    >
                      {renderLeftContent(tabItem, index)}
                    </div>
                    {!useArticleLayout &&
                      (tabItem.image || tabItem.rightCompound) && (
                        <div
                          className={`u-size1of2 u-flex u-flexCol${
                            tabItem.image && !tabItem.image.includes('<figure')
                              ? ''
                              : ' u-padding24'
                          }`}
                        >
                          {renderRightContent(tabItem, index)}
                        </div>
                      )}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>
    </Spacer>
  )
}
